import { Component, ViewChild, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserService, User } from '../../services/user.service';
import { LoggerService } from '../../core/services/logger.service';
import { catchError, finalize, filter } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { DeleteUserDialogComponent } from './delete-user-dialog/delete-user-dialog.component';

@Component({
    selector: 'app-users-page',
    imports: [CommonModule, FormsModule, RouterOutlet, MatCardModule, MatButtonModule, MatIconModule, MatMenuModule, MatPaginatorModule, MatTableModule, MatSortModule, MatSnackBarModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatTooltipModule],
    templateUrl: './users-page.component.html',
    styleUrls: ['./users-page.component.scss']
})
export class UsersPageComponent implements OnInit, AfterViewInit, OnDestroy {
    // Tabla de usuarios (migrada desde TeamMembers)
    displayedColumns: string[] = ['user', 'role', 'status', 'phone', 'createdAt', 'action'];
    dataSource = new MatTableDataSource<UserElement>([]);
    /** Término de búsqueda para filtrar por nombre o email */
    searchTerm = '';
    isLoading = false;
    private routerSubscription?: Subscription;
    private paginationRestored = false;
    private pendingPaginationState: { page: number; pageSize: number } | null = null;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.setupFilterPredicate();
        // Leer query params antes de cargar para saber si hay estado pendiente
        const queryParams = this.route.snapshot.queryParams;
        if (queryParams['page'] || queryParams['pageSize']) {
            const page = parseInt(queryParams['page'] || '1', 10);
            const pageSize = parseInt(queryParams['pageSize'] || '10', 10);
            this.pendingPaginationState = { page, pageSize };
            this.logger.info(`Estado de paginación pendiente detectado: page=${page}, pageSize=${pageSize}`, 'UsersPageComponent');
        }
        this.loadUsers();
        this.subscribeToRouterEvents();
    }

    /**
     * Configura el filtro de la tabla para buscar por nombre o email (insensible a mayúsculas).
     */
    private setupFilterPredicate(): void {
        this.dataSource.filterPredicate = (item: UserElement, filterValue: string): boolean => {
            const term = (filterValue || '').trim().toLowerCase();
            if (!term) {
                return true;
            }
            const name = (item.name || '').toLowerCase();
            const email = (item.email || '').toLowerCase();
            return name.includes(term) || email.includes(term);
        };
    }

    /**
     * Aplica el filtro rápido al listado mientras el usuario escribe (por nombre o email).
     */
    applyFilter(): void {
        this.dataSource.filter = this.searchTerm.trim();
        if (this.paginator) {
            this.paginator.firstPage();
        }
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    private subscribeToRouterEvents(): void {
        this.routerSubscription = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                if (url === '/users' || url.startsWith('/users?')) {
                    this.logger.info('Navegación detectada a /users, refrescando lista de usuarios', 'UsersPageComponent');
                    this.paginationRestored = false; // Resetear flag al navegar
                    
                    // Leer query params de la URL actual
                    const queryParams = this.route.snapshot.queryParams;
                    if (queryParams['page'] || queryParams['pageSize']) {
                        const page = parseInt(queryParams['page'] || '1', 10);
                        const pageSize = parseInt(queryParams['pageSize'] || '10', 10);
                        this.pendingPaginationState = { page, pageSize };
                        this.logger.info(`Estado de paginación pendiente: page=${page}, pageSize=${pageSize}`, 'UsersPageComponent');
                    }
                    
                    this.loadUsers();
                    // La paginación se restaurará automáticamente después de cargar los datos en loadUsers()
                }
            });
    }

    ngAfterViewInit(): void {
        // Asegurar que el paginador y sort estén asignados al dataSource
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        this.dataSource.sortingDataAccessor = (item: UserElement, property: string): string => {
            switch (property) {
                case 'user':
                    return item.name;
                case 'role':
                    return item.role;
                case 'status':
                    return item.status;
                case 'phone':
                    return item.phone;
                case 'createdAt':
                    return item.createdAt;
                default:
                    return (item as unknown as Record<string, string>)[property] ?? '';
            }
        };
        // La paginación se restaurará después de cargar los datos en loadUsers()
    }

    loadUsers(): void {
        this.logger.info('Iniciando carga de usuarios desde el backend', 'UsersPageComponent');
        this.userService.getUsers()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar usuarios', error as Error, 'UsersPageComponent');
                    this.snackBar.open('Error al cargar usuarios', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    return of({ success: false, data: [] });
                }),
                // eslint-disable-next-line no-unused-vars
                finalize(() => {})
            )
            .subscribe(response => {
                this.logger.info(`Respuesta del backend: ${JSON.stringify(response)}`, 'UsersPageComponent');
                if (response.success && response.data) {
                    const userElements = response.data.map((user: User) => this.mapUserToUserElement(user));
                    this.logger.info(`Mapeados ${userElements.length} usuarios para la tabla`, 'UsersPageComponent');
                    
                    // Asignar datos al dataSource
                    this.dataSource.data = userElements;
                    
                    // Reasignar el paginador al dataSource para forzar actualización del length
                    // Esto resuelve el problema donde el paginador muestra "0 de 0" después de navegar
                    if (this.paginator) {
                        // Guardar el pageSize actual antes de desvincular
                        const savedPageSize = this.paginator.pageSize;
                        
                        // Desvincular temporalmente para forzar actualización
                        this.dataSource.paginator = null;
                        
                        // Volver a vincular (esto hace que Angular Material actualice el length)
                        this.dataSource.paginator = this.paginator;
                        
                        // Restaurar el pageSize si era diferente del default
                        if (savedPageSize !== 10) {
                            this.paginator.pageSize = savedPageSize;
                        }
                        
                        this.logger.info(`Paginador reasignado - Length: ${this.paginator.length}, DataSource data length: ${this.dataSource.data.length}`, 'UsersPageComponent');
                    }
                    
                    // Forzar detección de cambios para actualizar el paginador
                    this.cdr.detectChanges();
                    
                    // Restaurar paginación después de que los datos estén cargados y el paginador esté disponible
                    setTimeout(() => {
                        this.restorePaginationStateAfterInit();
                    }, 100);
                } else {
                    this.logger.warn('Respuesta del backend no exitosa o sin datos', 'UsersPageComponent');
                }
            });
    }

    // Edit User
    editUser(userElement: UserElement): void {
        this.logger.info(`Editar usuario: ${userElement.name}`, 'UsersPageComponent');
        this.userService.getUsers()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al obtener datos del usuario', error as Error, 'UsersPageComponent');
                    this.snackBar.open('Error al obtener datos del usuario', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    return of({ success: false, data: [] });
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    const user = response.data.find((u: User) => u.email === userElement.email);
                    if (user) {
                        // Preservar estado de paginación en query params
                        const queryParams: any = {};
                        if (this.paginator) {
                            const currentPage = this.paginator.pageIndex;
                            const pageSize = this.paginator.pageSize;
                            if (currentPage > 0) {
                                queryParams.page = (currentPage + 1).toString();
                            }
                            if (pageSize !== 10) {
                                queryParams.pageSize = pageSize.toString();
                            }
                        }
                        this.router.navigate(['/users/edit', user.id], { queryParams });
                    } else {
                        this.snackBar.open('Usuario no encontrado', 'Cerrar', { duration: 3000, panelClass: ['error-snackbar'] });
                    }
                }
            });
    }

    // Delete User
    deleteUser(userElement: UserElement): void {
        this.logger.info(`Eliminar usuario: ${userElement.name}`, 'UsersPageComponent');
        this.userService.getUsers()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al obtener datos del usuario', error as Error, 'UsersPageComponent');
                    this.snackBar.open('Error al obtener datos del usuario', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    return of({ success: false, data: [] });
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    const user = response.data.find((u: User) => u.email === userElement.email);
                    if (user) {
                        this.openDeleteConfirmationDialog(user);
                    } else {
                        this.snackBar.open('Usuario no encontrado', 'Cerrar', { duration: 3000, panelClass: ['error-snackbar'] });
                    }
                }
            });
    }

    private openDeleteConfirmationDialog(user: User): void {
        const dialogRef = this.dialog.open(DeleteUserDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            disableClose: true,
            data: { user }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.performDeleteUser(user);
            }
        });
    }

    private performDeleteUser(user: User): void {
        this.logger.info(`Iniciando eliminación del usuario: ${user.email} (ID: ${user.id})`, 'UsersPageComponent');
        this.isLoading = true;
        this.userService.deleteUser(user.id)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al eliminar usuario', error as Error, 'UsersPageComponent');
                    const errorMessage = (error as any).error?.message || 'Error al eliminar el usuario';
                    this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    this.isLoading = false;
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe(response => {
                this.logger.info(`Respuesta de eliminación: ${JSON.stringify(response)}`, 'UsersPageComponent');
                if (response && response.success) {
                    this.logger.info('Usuario eliminado exitosamente, recargando lista', 'UsersPageComponent');
                    this.loadUsers();
                    this.snackBar.open('Usuario eliminado exitosamente', 'Cerrar', { duration: 3000, panelClass: ['success-snackbar'] });
                } else {
                    this.logger.warn('Respuesta de eliminación no exitosa', 'UsersPageComponent');
                }
            });
    }

    onCreateUser(): void {
        this.router.navigate(['/users/create']);
    }

    private mapUserToUserElement(user: User): UserElement {
        return {
            name: `${user.first_name} ${user.last_name}`,
            email: user.email,
            role: user.role,
            status: user.is_active ? 'active' : 'inactive',
            phone: user.phone,
            createdAt: new Date(user.created_at).toLocaleDateString('es-ES')
        };
    }

    // Mostrar listado sólo en la ruta base /users
    isBaseRoute(): boolean {
        const url = this.router.url || '';
        return url === '/users' || url === '/users/' || url.startsWith('/users?');
    }

    // Detectar ruta de creación /users/create
    isCreateRoute(): boolean {
        const url = this.router.url || '';
        return url.startsWith('/users/create');
    }

    /**
     * Restaura el estado de paginación después de que el paginador esté inicializado y los datos cargados
     */
    private restorePaginationStateAfterInit(): void {
        // Evitar restaurar múltiples veces
        if (this.paginationRestored) {
            return;
        }

        // Usar estado pendiente si está disponible, sino leer de query params
        const stateToRestore = this.pendingPaginationState || (() => {
            const queryParams = this.route.snapshot.queryParams;
            if (queryParams['page'] || queryParams['pageSize']) {
                return {
                    page: parseInt(queryParams['page'] || '1', 10),
                    pageSize: parseInt(queryParams['pageSize'] || '10', 10)
                };
            }
            return null;
        })();

        if (!stateToRestore) {
            return;
        }

        // Verificar que el paginador y los datos estén disponibles
        if (!this.paginator || !this.dataSource || this.dataSource.data.length === 0) {
            this.logger.debug('Paginador o datos no disponibles aún, reintentando...', 'UsersPageComponent');
            // Reintentar después de un breve delay
            setTimeout(() => {
                this.restorePaginationStateAfterInit();
            }, 50);
            return;
        }

        const { page, pageSize } = stateToRestore;
        
        this.logger.info(`Restaurando estado de paginación: page=${page}, pageSize=${pageSize}, totalItems=${this.dataSource.data.length}`, 'UsersPageComponent');
        
        // Restaurar tamaño de página primero
        if (pageSize !== 10) {
            this.paginator.pageSize = pageSize;
        }
        
        // Restaurar página (convertir de página 1-indexed a 0-indexed)
        // Solo restaurar si la página es válida (no excede el total de páginas)
        const totalPages = Math.ceil(this.dataSource.data.length / this.paginator.pageSize);
        if (page > 1 && page <= totalPages) {
            this.paginator.pageIndex = page - 1;
        } else if (page > totalPages && totalPages > 0) {
            // Si la página guardada excede el total, ir a la última página
            this.paginator.pageIndex = Math.max(0, totalPages - 1);
        }

        this.paginationRestored = true;
        this.pendingPaginationState = null;

        // Forzar actualización del paginador después de restaurar estado
        this.cdr.detectChanges();

        // Limpiar query params después de restaurar (sin recargar la página)
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true
        }).catch(() => {
            // Ignorar errores de navegación
        });
    }
}

export interface UserElement {
    name: string;
    email: string;
    role: string;
    status: string;
    phone: string;
    createdAt: string;
}